import { motion } from "framer-motion";
import { TrendingDown, Landmark, Snowflake, AlarmClock, ArrowRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { waLink, WA_MESSAGES } from "../lib/whatsapp";

const TRAPS = [
  {
    icon: TrendingDown,
    title: "Subavaliação de imóveis e empresas",
    text: "Sem avaliação técnica independente, imóveis, quotas societárias e investimentos entram na partilha por valores muito abaixo do real, e você descobre tarde demais.",
    stat: "até -35% no valor declarado",
    span: "lg:col-span-2",
  },
  {
    icon: Landmark,
    title: "Impostos excessivos (ITCMD & IR)",
    text: "Uma partilha mal estruturada gera ITCMD sobre o excedente de meação e ganho de capital desnecessário. Planejamento tributário prévio pode reduzir drasticamente essa conta.",
    stat: "economia potencial de 6 dígitos",
    span: "",
  },
  {
    icon: Snowflake,
    title: "Contas bloqueadas e falta de liquidez",
    text: "Liminar mal conduzida pode congelar contas, cartões e aplicações, deixando você sem acesso ao próprio dinheiro justamente quando mais precisa.",
    stat: "bloqueio em até 48h",
    span: "",
  },
  {
    icon: AlarmClock,
    title: "Acordos precipitados sob pressão emocional",
    text: "No calor do conflito, muita gente assina acordos irreversíveis para 'acabar logo'. Renunciar a direitos patrimoniais por pressa é o erro mais caro do divórcio.",
    stat: "decisões irreversíveis",
    span: "lg:col-span-2",
  },
];

export const PainPoints = () => (
  <section id="riscos" data-testid="pain-points-section" className="relative py-24 sm:py-32 bg-[#0B1320]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        eyebrow="As armadilhas que ninguém te conta"
        title="Os 4 ralos financeiros ocultos de um divórcio mal conduzido"
        description="A maioria das perdas patrimoniais não acontece na sentença. Acontece nos detalhes ignorados no caminho até ela."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-14" data-testid="pain-bento-grid">
        {TRAPS.map((t, i) => (
          <Reveal key={t.title} delay={i * 0.08} className={t.span}>
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ duration: 0.3 }}
              data-testid={`pain-card-${i}`}
              className="group h-full relative border border-amber-500/15 bg-gradient-to-b from-[#141F33] to-[#101A2C] rounded-2xl p-8 sm:p-9 overflow-hidden hover:border-amber-400/40 transition-colors duration-500"
            >
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-red-500/5 blur-3xl group-hover:bg-amber-400/10 transition-colors duration-700" />
              <div className="flex items-start justify-between gap-4">
                <div className="w-12 h-12 rounded-xl border border-amber-500/25 bg-[#0B1320] flex items-center justify-center shrink-0">
                  <t.icon className="w-6 h-6 text-amber-400" />
                </div>
                <span className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-red-400/80 border border-red-500/20 rounded-full px-3 py-1.5 whitespace-nowrap">
                  {t.stat}
                </span>
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-medium text-slate-100 mt-7 leading-snug">
                {t.title}
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed mt-4">{t.text}</p>
            </motion.div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2} className="mt-12 text-center">
        <motion.a
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          href={waLink(WA_MESSAGES.pain)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="pain-cta-whatsapp"
          className="inline-flex items-center gap-2.5 border border-amber-400/40 text-amber-200 font-semibold text-sm sm:text-base px-8 py-4 rounded-full hover:bg-amber-400/10 hover:border-amber-300 transition-colors duration-300"
        >
          Não quero cair nessas armadilhas
          <ArrowRight className="w-4 h-4" />
        </motion.a>
      </Reveal>
    </div>
  </section>
);
